import React from 'react';
import { useLocation } from 'react-router-dom';
import AppShell from './AppShell';

interface ConditionalAppShellProps {
  children: React.ReactNode;
  checkEmbed?: boolean; // Default true - whether to check for embed parameter
}

/**
 * ConditionalAppShell - Wrapper component that conditionally applies AppShell
 * based on the presence of 'embed=true' in the query parameters.
 * 
 * Usage:
 * <Route path="/merchant/:id" element={
 *   <ConditionalAppShell>
 *     <MerchantPublicProfile />
 *   </ConditionalAppShell>
 * } />
 * 
 * When ?embed=true is in the URL, the component renders without AppShell.
 * Otherwise, it wraps the children with AppShell for the full app experience.
 */
const ConditionalAppShell: React.FC<ConditionalAppShellProps> = ({ 
  children, 
  checkEmbed = true 
}) => {
  const location = useLocation();
  
  // Check if embed mode is enabled via query parameter
  const searchParams = new URLSearchParams(location.search);
  const isEmbedMode = checkEmbed && searchParams.get('embed') === 'true';
  
  // If in embed mode, render children directly without AppShell
  if (isEmbedMode) {
    return <>{children}</>;
  }
  
  // Otherwise, wrap with AppShell for the full app experience
  return (
    <AppShell>
      {children}
    </AppShell>
  );
};

export default ConditionalAppShell;
